/**
 * PLP (main-collection) page behaviors. Deferred via requestIdleCallback to reduce Total Blocking Time.
 */
function initMainCollectionPage() {
  /**
   * Collection Page JavaScript Functionality
   * 
   * Handles:
   * - Mobile sidebar toggle (drawer open/close)
   * - Filter group expand/collapse
   * - Filter search functionality
   * - Product image thumbnail switching
   */
  
  // Mobile sidebar toggle - Opens/closes filter sidebar on mobile
    const filterButton = document.querySelector('.main-collection__filter-button');
    const sidebar = document.querySelector('.main-collection__sidebar');
    const sidebarOverlay = document.querySelector('[data-collection-filter-overlay]');
    
    if (filterButton && sidebar) {
      filterButton.addEventListener('click', function() {
        sidebar.classList.toggle('main-collection__sidebar--open');
        if (sidebarOverlay) {
          sidebarOverlay.classList.toggle('backdrop-scrim--visible');
        }
        document.body.style.overflow = sidebar.classList.contains('main-collection__sidebar--open') ? 'hidden' : '';
      });
    }
    
    if (sidebarOverlay) {
      sidebarOverlay.addEventListener('click', function() {
        sidebar.classList.remove('main-collection__sidebar--open');
        sidebarOverlay.classList.remove('backdrop-scrim--visible');
        document.body.style.overflow = '';
      });
    }

    const sidebarCloseBtn = document.querySelector('.main-collection__sidebar-close');
    if (sidebarCloseBtn && sidebar) {
      sidebarCloseBtn.addEventListener('click', function() {
        sidebar.classList.remove('main-collection__sidebar--open');
        if (sidebarOverlay) {
          sidebarOverlay.classList.remove('backdrop-scrim--visible');
        }
        document.body.style.overflow = '';
      });
    }
    
    // Product image switching on thumbnail click
    window.switchProductImage = function(thumb, productId, event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      const imageIndex = thumb.getAttribute('data-thumbnail-index');
      const imageIndexNum = parseInt(imageIndex);
      
      if (!isNaN(imageIndexNum) && window.goToProductImage) {
        window.goToProductImage(productId, imageIndexNum, event);
        
        // Update active thumbnail state
        const container = thumb.closest('.product-thumbnails');
        if (container) {
          container.querySelectorAll('.product-thumbnail').forEach(t => t.classList.remove('active'));
          thumb.classList.add('active');
        }
      }
    };

    window.showNextThumbnail = function(button, productId, event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      if (window.navigateProductCarousel) {
        window.navigateProductCarousel(productId, 'next', event);
      }
    };

    // Thumbnail-click and second-image-hover setup used to run here, scanning every card twice.
    // Both targeted the legacy `product-card-collection` markup (#product-image-<id>,
    // .product-thumbnail, data-gallery-images), which the current card-gallery slideshow no longer
    // renders, so every iteration bailed out immediately. Hover previews are handled by
    // product-card.js and the slideshow component.

    // Variant thumbnail click handling using event delegation
    document.addEventListener('click', function(e) {
      const variantThumbnail = e.target.closest('.product-variant-thumbnail');
      if (variantThumbnail && variantThumbnail.hasAttribute('data-variant-image-url')) {
        e.preventDefault();
        e.stopPropagation();
        
        if (window.switchToVariantImage) {
          window.switchToVariantImage(variantThumbnail, e);
        }
        return false;
      }
    });
    
    /**
     * Parse the size selector's variant data.
     *
     * @description quick-add.liquid serialises variants as `id:color:size:available` entries joined by
     *              `|`, rather than JSON, to keep the attribute small across a full page of results.
     *              Entries that don't have exactly four fields are skipped, so a colour or size
     *              containing a colon can never be matched to the wrong variant id.
     *
     * @param {string} variantsData - The raw data-variants attribute value
     * @returns {Array<{id: string, color: string, size: string, available: boolean}>}
     */
    function parseVariantsData(variantsData) {
      /** @type {Array<{id: string, color: string, size: string, available: boolean}>} */
      const variants = [];

      String(variantsData).split('|').forEach(function(entry) {
        if (!entry) return;

        const parts = entry.split(':');
        if (parts.length !== 4) return;

        variants.push({
          id: parts[0] || '',
          color: parts[1] || '',
          size: parts[2] || '',
          available: parts[3] === 'true'
        });
      });

      return variants;
    }

    /**
     * Update size options based on selected color variant
     * 
     * @description Dynamically shows/hides and enables/disables size buttons based on
     *              which sizes are available for the currently selected color variant.
     *              Parses variant data from the size selector's data-variants attribute and
     *              matches sizes to the selected color.
     * 
     * @param {HTMLElement} card - The product card element containing size selector
     * @param {string} selectedColor - The color value to filter sizes by (e.g., "White", "Yellow")
     * @returns {void}
     */
    function initializeSizeOptionsForColor(card, selectedColor) {
      if (!card || !selectedColor) return;
      
      const sizeSelector = card.querySelector('.product-card__size-selector');
      if (!sizeSelector) {
        // Size selector might not exist if product doesn't have size option
        return;
      }
      
      const variantsData = sizeSelector.getAttribute('data-variants');
      if (!variantsData || variantsData.trim() === '') {
        return;
      }
      
      try {
        const variants = parseVariantsData(variantsData);
        if (variants.length === 0) {
          return;
        }
        
        const sizeOptions = sizeSelector.querySelectorAll('.product-card__size-option');
        if (sizeOptions.length === 0) {
          return;
        }
        
        // Normalize color for comparison (trim whitespace)
        const normalizedSelectedColor = String(selectedColor).trim();
        
        // Update each size option
        sizeOptions.forEach(function(sizeOption) {
          const sizeValue = sizeOption.getAttribute('data-size');
          if (!sizeValue) return;
          
          const normalizedSizeValue = String(sizeValue).trim();
          
          // Find variant with this color and size (exact match after trimming)
          // Try to find available variant first
          let matchingVariant = variants.find(function(v) {
            if (!v || v.color === undefined || v.size === undefined) return false;
            const variantColor = String(v.color).trim();
            const variantSize = String(v.size).trim();
            return variantColor === normalizedSelectedColor && variantSize === normalizedSizeValue && v.available === true;
          });
          
          // If no available variant found, still find the variant (will be disabled)
          if (!matchingVariant) {
            matchingVariant = variants.find(function(v) {
              if (!v || v.color === undefined || v.size === undefined) return false;
              const variantColor = String(v.color).trim();
              const variantSize = String(v.size).trim();
              return variantColor === normalizedSelectedColor && variantSize === normalizedSizeValue;
            });
          }
          
          if (matchingVariant) {
            // Show and update variant ID and color
            sizeOption.style.display = '';
            sizeOption.setAttribute('data-variant-id', matchingVariant.id);
            sizeOption.setAttribute('data-color', normalizedSelectedColor);
            sizeOption.classList.remove('product-card__size-option--hidden');
            
            if (matchingVariant.available) {
              // Enable button if variant is available
              sizeOption.disabled = false;
              sizeOption.classList.remove('product-card__size-option--unavailable');
              sizeOption.classList.remove('product-card__size-option--added');
              sizeOption.setAttribute('aria-label', 'Size ' + sizeValue);
            } else {
              // Disable button if variant exists but is unavailable
              sizeOption.disabled = true;
              sizeOption.classList.add('product-card__size-option--unavailable');
              sizeOption.classList.remove('product-card__size-option--added');
              sizeOption.setAttribute('aria-label', 'Size ' + sizeValue + ' - Unavailable');
            }
          } else {
            // Hide button if no matching variant exists for this color/size combination
            sizeOption.style.display = 'none';
            sizeOption.classList.add('product-card__size-option--hidden');
            sizeOption.removeAttribute('data-variant-id');
            sizeOption.disabled = true;
            sizeOption.classList.add('product-card__size-option--unavailable');
            sizeOption.classList.remove('product-card__size-option--added');
            sizeOption.setAttribute('aria-label', 'Size ' + sizeValue + ' - Unavailable');
          }
        });
      } catch (e) {
        console.error('Error updating size options:', e);
        console.error('Variants data:', variantsData);
      }
    }
    
    function initializeDefaultProductCardState() {
      document.querySelectorAll('.product-card').forEach(function(card) {
        const activeSwatch = card.querySelector('.product-card__swatch--active');
        if (activeSwatch) {
          const selectedColor = activeSwatch.getAttribute('data-variant-color');
          const swatchVariantId = activeSwatch.getAttribute('data-variant-id');

          if (selectedColor) {
            initializeSizeOptionsForColor(card, selectedColor);
          }

          const quickAddButton = card.querySelector('.product-card__quick-add-button');
          if (quickAddButton && swatchVariantId) {
            quickAddButton.setAttribute('data-variant-id', swatchVariantId);
          }
        }
      });
    }

    let deferredProductCardSetupScheduled = false;

    function scheduleDeferredProductCardSetup() {
      if (deferredProductCardSetupScheduled) return;
      deferredProductCardSetupScheduled = true;

      const run = function() {
        initializeDefaultProductCardState();
        applyRatingOverlayOnImage();

        const productsContainer = document.querySelector('.main-collection__products');
        if (productsContainer && !productsContainer.dataset.iwRatingObserverAttached) {
          productsContainer.dataset.iwRatingObserverAttached = 'true';
          const observer = new MutationObserver(function() {
            applyRatingOverlayOnImage();
          });
          observer.observe(productsContainer, { childList: true, subtree: true });
        }
      };

      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(run, { timeout: 2000 });
      } else {
        setTimeout(run, 150);
      }
    }

    scheduleDeferredProductCardSetup();
    
    /**
     * Re-point quick add buttons at the newly selected colour.
     *
     * @description Size buttons are rendered for whichever colour was selected server-side, so without
     *              this a shopper who switches colour and then picks a size adds the original colour to
     *              the cart. The variant:update event bubbles from the swatch picker, and product-card
     *              handles it on itself, so by the time it reaches the document the picker's checked
     *              input already reflects the new colour.
     *
     * @listens variant:update
     * @param {Event} e - The variant update event
     * @returns {void}
     */
    document.addEventListener('variant:update', function(e) {
      const target = /** @type {Element | null} */ (e.target);
      if (!target || typeof target.closest !== 'function') return;
      
      const card = /** @type {HTMLElement | null} */ (target.closest('.product-card'));
      if (!card) return;
      
      const checkedSwatch = /** @type {HTMLInputElement | null} */ (
        card.querySelector('swatches-variant-picker-component input[type="radio"]:checked')
      );
      const selectedColor = checkedSwatch ? checkedSwatch.value : '';
      if (!selectedColor || !checkedSwatch) return;
      
      initializeSizeOptionsForColor(card, selectedColor);
      
      const quickAddButton = card.querySelector('.product-card__quick-add-button');
      const selectedVariantId = checkedSwatch.getAttribute('data-variant-id');
      if (quickAddButton && selectedVariantId) {
        quickAddButton.setAttribute('data-variant-id', selectedVariantId);
      }
    });
    
    /**
     * Color swatch click handler - updates product image, price, and available sizes
     * 
     * @description Event delegation handler for color variant swatch clicks.
     *              When a color swatch is clicked:
     *              1. Updates main product image to variant-specific image
     *              2. Updates product price (regular or sale price)
     *              3. Updates active swatch visual state
     *              4. Filters size options to show only sizes available for selected color
     *              5. Updates Quick Add button variant ID (for single-option products)
     *              6. Resets carousel to first image
     * 
     * @listens click
     * @param {Event} e - Click event object
     * @returns {boolean|void} Returns false to prevent default behavior
     */
    // Color swatch click handling using event delegation
    document.addEventListener('click', function(e) {
      const swatch = e.target.closest('.product-card__swatch, .collection-page__swatch');
      if (swatch && swatch.hasAttribute('data-variant-image-url')) {
        if (swatch.disabled || swatch.getAttribute('aria-disabled') === 'true' || swatch.classList.contains('collection-page__swatch--disabled')) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        
        const card = swatch.closest('.product-card');
        if (!card) return false;
        
        const productId = card.getAttribute('data-product-id');
        if (!productId) return false;
        
        const variantImageUrl = swatch.getAttribute('data-variant-image-url');
        if (!variantImageUrl) return false;
        
        const mainImage = card.querySelector('#product-image-' + productId);
        if (!mainImage) return false;
        
        // Update main image with variant image
        mainImage.src = variantImageUrl;
        mainImage.srcset = variantImageUrl.replace('width=684', 'width=342') + ' 1x, ' + variantImageUrl + ' 2x';
        mainImage.setAttribute('data-image-index', '0'); // Reset to first image index
        
        // Update product price
        const variantPrice = swatch.getAttribute('data-variant-price');
        const variantCompareAtPrice = swatch.getAttribute('data-variant-compare-at-price');
        const variantPriceCents = swatch.getAttribute('data-variant-price-cents');
        const variantCompareAtPriceCents = swatch.getAttribute('data-variant-compare-at-price-cents');
        
        if (variantPrice) {
          const priceSection = card.querySelector('.product-card__price-section');
          if (priceSection) {
            const priceValue = variantPriceCents ? parseInt(variantPriceCents) : 0;
            const compareAtPriceValue = variantCompareAtPriceCents ? parseInt(variantCompareAtPriceCents) : 0;
            
            if (compareAtPriceValue > priceValue && compareAtPriceValue > 0) {
              // Show sale price with compare at price
              priceSection.innerHTML = '<span class="product-card__price-sale">' + variantPrice + '</span>' +
                                      '<span class="product-card__price-compare">' + variantCompareAtPrice + '</span>';
            } else {
              // Show regular price
              priceSection.innerHTML = '<span class="product-card__price-regular">' + variantPrice + '</span>';
            }
          }
        }
        
        // Update active swatch
        const swatches = card.querySelectorAll('.product-card__swatch');
        swatches.forEach(function(s) {
          s.classList.remove('product-card__swatch--active');
        });
        swatch.classList.add('product-card__swatch--active');
        
        // Get selected color
        const selectedColor = swatch.getAttribute('data-variant-color');
        const swatchVariantId = swatch.getAttribute('data-variant-id');
        
        // Update size options based on selected color (if size selector exists)
        if (selectedColor) {
          initializeSizeOptionsForColor(card, selectedColor);
        }
        
        // Update Quick Add button variant ID if it exists (for single-option products)
        const quickAddButton = card.querySelector('.product-card__quick-add-button');
        if (quickAddButton && swatchVariantId) {
          quickAddButton.setAttribute('data-variant-id', swatchVariantId);
        }
        
        // Update active carousel dot (reset to first)
        if (window.updateCarouselDots) {
          window.updateCarouselDots(card, 0);
        }
        
        return false;
      }
    });

    /**
     * Rating overlay on image: move review block into card gallery for bottom-left overlay.
     * Runs on load and when new product cards are added (e.g. infinite scroll).
     * Deferred to idle because it is decorative and not required for first card interaction.
     */
    function applyRatingOverlayOnImage() {
      const mainCollection = document.querySelector('.main-collection');
      if (!mainCollection) return;
      const cards = mainCollection.querySelectorAll('product-card[data-rating-overlay-on-image="true"], .product-card[data-rating-overlay-on-image="true"]');
      cards.forEach(function(card) {
        const content = card.querySelector('.product-card__content');
        if (!content) return;
        const reviewBlock = content.querySelector('.review-block:not(.card-gallery__rating-overlay)');
        const gallery = content.querySelector('card-gallery.card-gallery, .card-gallery');
        if (!reviewBlock || !gallery) return;
        reviewBlock.classList.add('card-gallery__rating-overlay');
        gallery.appendChild(reviewBlock);
      });
    }

    /**
     * Switch to variant image when variant thumbnail is clicked
     *
     * @param {HTMLElement} variantThumbnail - The clicked variant thumbnail element
     * @param {Event} event - Click event object
     * @returns {void}
     */
    window.switchToVariantImage = function(variantThumbnail, event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    const card = variantThumbnail.closest('.product-card');
    if (!card) return;
    
    const productId = card.getAttribute('data-product-id');
    if (!productId) return;
    
    const mainImage = card.querySelector('#product-image-' + productId);
    if (!mainImage) return;
    
    const variantImageUrl = variantThumbnail.getAttribute('data-variant-image-url');
    if (!variantImageUrl) return;
    
    // Update main image with variant image
    mainImage.src = variantImageUrl;
    mainImage.srcset = variantImageUrl.replace('width=684', 'width=342') + ' 1x, ' + variantImageUrl + ' 2x';
    mainImage.setAttribute('data-image-index', '0'); // Reset to first image index
    
    // Update active variant thumbnail
    const variantThumbnails = card.querySelectorAll('.product-variant-thumbnail');
    variantThumbnails.forEach(function(t) {
      t.classList.remove('active');
    });
    variantThumbnail.classList.add('active');
    
    // Update active carousel dot (reset to first)
    if (window.updateCarouselDots) {
      window.updateCarouselDots(card, 0);
    }
  };

  /**
   * Quick Add button click handler - adds product to cart via AJAX
   * 
   * @description Event delegation handler for Quick Add button clicks.
   *              Adds the product variant to cart without page reload:
   *              1. Shows loading state ("Adding...")
   *              2. Sends AJAX request to /cart/add.js
   *              3. Dispatches cart:updated and cart:add custom events
   *              4. Opens minicart automatically
   *              5. Shows success state ("Added!") then resets
   * 
   * @listens click
   * @param {Event} e - Click event object
   * @returns {void}
   * 
   * @fires cart:updated - Dispatched after successful cart addition
   * @fires cart:add - Dispatched for cart drawer auto-open functionality
   */
  // Handle Quick Add button clicks
  document.addEventListener('click', function(e) {
    const quickAddButton = e.target.closest('.product-card__quick-add-button');
    if (!quickAddButton || !quickAddButton.hasAttribute('data-variant-id')) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const variantId = quickAddButton.getAttribute('data-variant-id');
    if (!variantId) return;

    const originalText = quickAddButton.textContent;
    quickAddButton.disabled = true;

    // Add to cart via AJAX
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: { _itemSource: 'FPC' }
      })
    })
    .then(response => {
      if (!response.ok) {
        return response.json().then(err => Promise.reject(err));
      }
      return response.json();
    })
    .then(data => {
      // Keep original button text (e.g. "Quick Add"); do not switch to "Added!"

      // Trigger cart update event for cart icon updates
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: data
      }));

      // Dispatch CartAddEvent for cart drawer auto-open (if available)
      // The cart drawer listens for CartAddEvent.eventName
      const cartAddEvent = new CustomEvent('cart:add', {
        bubbles: true,
        detail: data
      });
      document.dispatchEvent(cartAddEvent);

      // Open cart drawer directly
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }

      quickAddButton.disabled = false;
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
      quickAddButton.textContent = 'Error';
      setTimeout(() => {
        quickAddButton.textContent = originalText;
        quickAddButton.disabled = false;
      }, 2000);
    });
  });

  /**
   * Size option click handler - adds specific size variant to cart via AJAX
   * 
   * @description Event delegation handler for size option button clicks.
   *              Adds the specific color+size variant combination to cart:
   *              1. Validates button is enabled and has variant ID
   *              2. Sends AJAX request to /cart/add.js
   *              3. Dispatches cart:updated and cart:add custom events
   *              4. Opens minicart automatically
   *              5. Applies visual feedback (blue background, white text)
   *              Note: Button text does NOT change (only visual state)
   * 
   * @listens click
   * @param {Event} e - Click event object
   * @returns {void}
   * 
   * @fires cart:updated - Dispatched after successful cart addition
   * @fires cart:add - Dispatched for cart drawer auto-open functionality
   */
  // Handle Size Option clicks
  document.addEventListener('click', function(e) {
    const sizeOption = e.target.closest('.product-card__size-option');
    if (!sizeOption || sizeOption.disabled || !sizeOption.hasAttribute('data-variant-id')) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const variantId = sizeOption.getAttribute('data-variant-id');
    if (!variantId) return;

    // Add to cart via AJAX
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: { _itemSource: 'FPC' }
      })
    })
    .then(response => {
      if (!response.ok) {
        return response.json().then(err => Promise.reject(err));
      }
      return response.json();
    })
    .then(data => {
      // Add active state - visual feedback only
      sizeOption.classList.add('product-card__size-option--added');
      
      // Trigger cart update event for cart icon updates
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: data
      }));

      // Dispatch CartAddEvent for cart drawer auto-open (if available)
      const cartAddEvent = new CustomEvent('cart:add', {
        bubbles: true,
        detail: data
      });
      document.dispatchEvent(cartAddEvent);

      // Open cart drawer directly
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
    });
  });

}

function scheduleMainCollectionPageInit() {
  if (window.Shopify && window.Shopify.designMode) {
    initMainCollectionPage();
    return;
  }
  const run = () => initMainCollectionPage();
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(run, { timeout: 2500 });
  } else {
    run();
  }
}

function onMainCollectionReady() {
  scheduleMainCollectionPageInit();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', onMainCollectionReady, { once: true });
} else {
  onMainCollectionReady();
}
